import './DashboardsList.scss';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAutoAwesome, MdDeleteOutline, MdOutlineAddChart } from 'react-icons/md';
import { DashboardId, DashboardMeta } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { Page } from '@Components/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { Spinner } from '@Components/Spinner/Spinner';
import { IconButton } from '@Components/IconButton/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Appearance, Intent } from '@Components/designConstants';
import { useRecordPermissions } from '../Charts/hooks/useRecordPermissions';
import { ViewOnlyBadge } from '../Charts/ViewOnlyBadge';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useSetAreYouSure } from '../../redux/modals';
import { useDashboardAgentDrawer } from '../../redux/dashboardAgent';
import { useDashboardList, useDashboardTemplates, useDeleteDashboard } from './hooks/useDashboard';
import { TemplateGalleryModal } from './TemplateGalleryModal';

// All dashboards for the client: open one, create a new one, or delete. Charts live in their own
// library (/charts) — this page is only the dashboard containers.
export const DashboardsList = () => {
  const navigate = useNavigate();
  const { cid } = useCompanyInfo();
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  /*
  const { mutateAsync: createDashboard, isPending: isCreating } = useCreateDashboard(cid);
  const setError = useSetError();

  const handleCreate = async () => {
    try {
      const created = await createDashboard('Untitled Dashboard');
      navigate(`/${cid}/dashboard/${created.id}`);
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
  };
  */

  return (
    <Page title={PAGE_NAMES.DASHBOARDS} pageType={PAGE_NAMES.DASHBOARDS}>
      <div className='dashboards-list'>
        {/* <div className='dashboards-list-header'>
          <Button
            design={Design.BRAND}
            size={Size.SM}
            leadingElement={<MdAdd />}
            disabled={isCreating}
            onClick={handleCreate}
          >
            New Dashboard
          </Button>
        </div> */}
        <DashboardsListBody
          cid={cid}
          onOpen={id => navigate(`/${cid}/dashboard/${id}`)}
          onOpenTemplateGallery={() => setShowTemplateGallery(true)}
        />
      </div>
      {showTemplateGallery && (
        <TemplateGalleryModal cid={cid} onClose={() => setShowTemplateGallery(false)} />
      )}
    </Page>
  );
};

interface DashboardsListBodyProps {
  cid: string;
  onOpen: (id: DashboardId) => void;
  onOpenTemplateGallery: () => void;
}

const DashboardsListBody = ({ cid, onOpen, onOpenTemplateGallery }: DashboardsListBodyProps) => {
  const { data: dashboards, isLoading, isError } = useDashboardList(cid);
  const { mutate: deleteDashboard, isPending: isDeleting } = useDeleteDashboard(cid);
  // Fetched HERE, not only in the modal, so the card can be hidden when this client has no
  // eligible templates. Same react-query key the modal uses, so opening it is a cache hit rather
  // than a second request.
  const { data: templates } = useDashboardTemplates(cid);
  const setAreYouSure = useSetAreYouSure();
  const { setDrawerOpen } = useDashboardAgentDrawer();

  const confirmDelete = (dashboard: DashboardMeta) => {
    setAreYouSure({
      intent: Intent.ERROR,
      title: 'Delete dashboard?',
      message: `"${dashboard.name}" will be permanently deleted. This can't be undone.`,
      okayText: 'Delete',
      onOkay: () => deleteDashboard(dashboard.id)
    });
  };

  if (isLoading) return <Spinner />;

  // The template card is the last grid item whenever this client HAS eligible templates — error or
  // empty dashboard list included, since a client with no dashboards yet is this feature's whole
  // audience and neither state should be a dead end. It is hidden when the gallery would be empty:
  // an entry point onto "No templates are available for this company yet." is the very dead end
  // this card exists to avoid. Hidden rather than disabled — there is nothing the viewer could do
  // to make templates appear, so a disabled control would only raise a question it can't answer.
  return (
    <>
      {isError && <div className='dashboards-list-message'>Couldn&apos;t load dashboards.</div>}
      <div className='dashboards-list-grid'>
        {dashboards?.map(dashboard => (
          <DashboardListCard
            key={dashboard.id}
            dashboard={dashboard}
            deleteDisabled={isDeleting}
            onOpen={() => onOpen(dashboard.id)}
            onDelete={() => confirmDelete(dashboard)}
          />
        ))}
        <div className='dashboards-list-create'>
          {Boolean(templates?.length) && <TemplateGalleryCard onClick={onOpenTemplateGallery} />}
          <AgentCard onClick={() => setDrawerOpen(true)} />
        </div>
      </div>
    </>
  );
};

interface TemplateGalleryCardProps {
  onClick: () => void;
}

// The gallery's entry point, styled like a real dashboard card so it reads as one more thing to
// open rather than a form control bolted onto the grid.
const TemplateGalleryCard = ({ onClick }: TemplateGalleryCardProps) => (
  <InteractiveDiv className='dashboards-list-card dashboards-list-card-template' onClick={onClick}>
    <MdOutlineAddChart />
    <span className='dashboards-list-card-name'>Start from a template</span>
  </InteractiveDiv>
);

// Narrower than the template card and without the flex:1 name, so the two read as a primary
// action plus a secondary one rather than as two equal choices.
const AgentCard = ({ onClick }: TemplateGalleryCardProps) => (
  <InteractiveDiv className='dashboards-list-card dashboards-list-card-agent' onClick={onClick}>
    <MdAutoAwesome />
    <span>Build with agent</span>
  </InteractiveDiv>
);

interface DashboardListCardProps {
  dashboard: DashboardMeta;
  deleteDisabled: boolean;
  onOpen: () => void;
  onDelete: () => void;
}

// The card works its own permission out. `DashboardMeta` already carries both role levels, so
// nothing extra is fetched and no prop is threaded down from the list.
const DashboardListCard = ({
  dashboard,
  deleteDisabled,
  onOpen,
  onDelete
}: DashboardListCardProps) => {
  const { isResolved, canEdit } = useRecordPermissions(dashboard);

  return (
    <InteractiveDiv className='dashboards-list-card' onClick={onOpen}>
      <span className='dashboards-list-card-name'>{dashboard.name}</span>
      {dashboard.isDefault && <span className='dashboards-list-card-badge'>Default</span>}
      {isResolved && !canEdit && <ViewOnlyBadge />}
      {/* RENDER the delete control only when this user may delete, rather than disabling it. A
          greyed button with no explanation is worse than no button, and a disabled IconButton's
          tooltip would not fire anyway — a disabled <button> emits no pointer events. The
          in-flight guard folds into the same condition instead of sitting beside it as a second
          gate with a different meaning. */}
      {canEdit && (
        <IconButton
          className='dashboards-list-card-delete'
          appearance={Appearance.EMPTY}
          disabled={deleteDisabled}
          onClick={e => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <MdDeleteOutline />
        </IconButton>
      )}
    </InteractiveDiv>
  );
};
